import React, { useState } from 'react';
import { StripeCustomerAddressDto, TaxIdDto, TaxIdStatus } from '../dto';
import { DivButton } from '../../commons/components/DivButton';
import { analyticsTrack } from '../../web-analytics/webAnalytics';
import { AnalyticsEvent } from '../../web-analytics/AnalyticsEvent';
import { useMutation } from '@tanstack/react-query';
import useAnalyticsPage from '../../web-analytics/hooks/useAnalyticsPage';
import { Page } from '../../web-analytics/Page';
import useApi from '../../api/backendApiContext';

import { openUrlInNewTab } from '../../util/window-utils';
import { Customer } from '../types';
import { useIsTabletOrLarger } from '../../responsive/hooks/hooks';
import { SmsNotificationSwitch } from './SmsNotificationSwitch';
import { Button } from 'react-bootstrap';
import { ConfirmModal } from '../../commons/components/ConfirmModal';
import { v4 as uuidv4 } from 'uuid';
import { useNotification } from '../../notification/notificationsContext';
import { useQueryCacheManager } from '../../hooks/useQueryCacheManager';
import clsx from 'clsx';

const AddressTile = ({ address }: { address: StripeCustomerAddressDto }) => {
  return (
    <>
      {!!address.line1 && (
        <div>
          <span className="growegy-label14--moderrat" data-test="billing-details__address-line1">
            {address.line1}
          </span>
        </div>
      )}
      {!!address.line2 && (
        <div>
          <span className="growegy-label14--moderrat" data-test="billing-details__address-line2">
            {address.line2}
          </span>
        </div>
      )}
      <div>
        <span
          className="growegy-label14--moderrat"
          data-test="billing-details__address-line3"
        >{`${address.city}, ${address.state} ${address.postalCode} ${address.country}`}</span>
      </div>
    </>
  );
};

const ErrorMessage = (props: { text: string; dateTest: string }) => {
  const { text, dateTest } = props;
  return (
    <span className="billing__error-message" data-test={dateTest}>
      {text}
    </span>
  );
};

const AddEinDialog = () => {
  useAnalyticsPage(Page.ADD_EIN);

  const [ein, setEin] = useState('');
  const { notifySuccess, notifyError } = useNotification();
  const { billingApi } = useApi();
  const queryCacheManager = useQueryCacheManager();
  const { mutate: setEinMutation } = useMutation({
    mutationFn: async (taxId: TaxIdDto) => billingApi.setTaxId(taxId),
    onSuccess: async (data) => {
      await queryCacheManager.updateBillingCustomer({ taxId: data });
      notifySuccess({
        notificationMsg: 'EIN was successfully set.',
        logMsg: 'EIN was successfully set.',
      });
      analyticsTrack(AnalyticsEvent.TAX_ID_ADDED);
    },
    onError: () =>
      notifyError({
        notificationMsg:
          'Something went wrong while setting EIN, please try again later or contact support.',
        logMsg: 'Something went wrong while setting EIN.',
      }),
  });

  return (
    <ConfirmModal
      title="Add Employer Identification Number (EIN)"
      className="growegy-modal--confirm-dialog"
      footer={{
        type: 'show',
        applyButton: {
          label: 'Add EIN',
          disabled: !ein.match(/^\d{2}-?\d{7}$/)?.length,
          onClick: () => setEinMutation({ value: ein, status: 'AVAILABLE' }),
        },
        cancelButton: { label: 'Cancel', onClick: () => {} },
      }}
    >
      <div className="growegy-form__row form-group">
        <div className="growegy-form__label">EIN</div>
        <div className="billing__ein-input-wrapper">
          <input
            type="text"
            value={ein}
            placeholder="Enter EIN"
            maxLength={10}
            onChange={(e) => {
              let value = e.target.value.replace(/[^0-9-]/g, '');
              if (value.match(/^\d{3,}$/)) {
                value = `${value.substring(0, 2)}-${value.substring(2)}`;
              }
              setEin(value);
            }}
            className="billing__ein-input fs-mask"
            data-test="billing__ein-input"
            autoFocus={true}
          />
          <div className="billing__ein-input-help">
            Can be found on your IRS form SS‑4, the format is 12‑1234567
          </div>
        </div>
      </div>
    </ConfirmModal>
  );
};

export const AddTaxIdButton = (props: {
  taxIdStatus: TaxIdStatus;
  dateTest: string;
  text?: string;
}) => {
  const { taxIdStatus, text, dateTest } = props;
  const [modalKey, setModalKey] = useState<string | null>(null);
  return (
    <>
      <Button
        variant={taxIdStatus === 'UNKNOWN' ? 'danger' : 'primary'}
        className={clsx(
          'flex-shrink-0',
          taxIdStatus === 'UNKNOWN' ? 'button-destructive' : 'button-apply'
        )}
        onClick={() => setModalKey(uuidv4())}
        data-test={dateTest}
      >
        {text ?? 'Add EIN'}
      </Button>
      {modalKey && <AddEinDialog key={modalKey} />}
    </>
  );
};

const UpdateTaxIdButton = () => {
  const [modalKey, setModalKey] = useState<string | null>(null);
  return (
    <>
      <Button
        variant="light"
        className="button-cancel billing-table__inline-button"
        onClick={() => setModalKey(uuidv4())}
        data-test="billing__update-ein-button"
      >
        Update
      </Button>
      {modalKey && (
        <ConfirmModal
          title="Your EIN can’t be updated in the app"
          key={modalKey}
          footer={{
            type: 'show',
            applyButton: {
              label: 'Got it',
              onClick: () => {},
            },
          }}
          onClose={() => {}}
        >
          <div className="growegy-title14--moderat">
            Please contact us at <a href="mailto:support@growegy.com">support@growegy.com</a>, and
            our support team will assist you.
          </div>
        </ConfirmModal>
      )}
    </>
  );
};

const CompanyNameDetails = (props: { companyName: string; labelClasses: string }) => {
  const { companyName, labelClasses } = props;
  const [modalKey, setModalKey] = useState<string | null>(null);
  return (
    <>
      <div className="billing-flex-table__row">
        <div className={labelClasses}>Company name</div>
        <div className="col-9 billing-table__column--small-padding">
          <span className="growegy-label14--moderrat" data-test="billing-details__company-name">
            {companyName}
          </span>
          <Button
            variant="light"
            className="button-cancel billing-table__inline-button"
            onClick={() => setModalKey(uuidv4())}
            data-test="billing__update-company-name-button"
          >
            Update
          </Button>
        </div>
      </div>

      {modalKey && (
        <ConfirmModal
          title="Your company name cannot be updated in the app"
          key={modalKey}
          footer={{
            type: 'show',
            applyButton: {
              label: 'Got it',
              onClick: () => {},
            },
          }}
          onClose={() => {}}
        >
          <div className="growegy-title14--moderat">
            Please contact us at <a href="mailto:support@growegy.com">support@growegy.com</a>, and
            our support team will assist you.
          </div>
        </ConfirmModal>
      )}
    </>
  );
};

const TaxIdDetails = (props: { taxId: TaxIdDto; labelClasses: string }) => {
  const { taxId, labelClasses } = props;
  return (
    <>
      <div className="billing-flex-table__row">
        <div className={labelClasses}>EIN</div>
        <div className="col-9 billing-table__column--small-padding">
          {taxId.status === 'UNKNOWN' || taxId.status === 'NOT_AVAILABLE' || !taxId.value ? (
            <AddTaxIdButton taxIdStatus={taxId.status} dateTest="billing__add-ein-button" />
          ) : (
            <>
              <span className="growegy-label14--moderrat" data-test="billing-details__ein">
                {taxId.value}
              </span>
              {taxId.status === 'AVAILABLE' && <UpdateTaxIdButton />}
            </>
          )}
        </div>
      </div>
    </>
  );
};

const PhoneDetails = (props: { phone: string }) => {
  const { phone } = props;
  return (
    <div className="billing__phone-number-container">
      {phone.length > 0 ? (
        <span className="growegy-label14--moderrat" data-test="billing-details__phone">
          {phone}
        </span>
      ) : (
        <ErrorMessage
          text="Phone number is missing"
          dateTest="billing__phone-number-missing-error"
        />
      )}
      <SmsNotificationSwitch />
    </div>
  );
};

export const BillingDetailsSection = ({ customer }: { customer: Customer }) => {
  useAnalyticsPage(Page.BILLING);
  const { billingApi } = useApi();

  const editBillingPersonalInformation = async () => {
    const billingPortalSession = await billingApi.createBillingPortalSession();
    logger.info('Redirecting to billing portal');
    openUrlInNewTab(
      billingPortalSession.url,
      {
        analyticsEvent: AnalyticsEvent.BILLING_BILLING_INFORMATION_CHANGE_OPEN,
      },
      '/customer/update'
    );
  };

  const { isLoading, mutate: openBillingBillingInfomationChangeMutation } = useMutation(
    editBillingPersonalInformation,
    {
      onError: () => {
        analyticsTrack(AnalyticsEvent.BILLING_PORTAL_FAILED);
      },
    }
  );

  const isTabletOrLarger = useIsTabletOrLarger();

  const labelClasses = 'col-3 billing-table__column align-self-start growegy-title14--moderat';
  const valueClasses = 'col-9 billing-table__column';
  const onUpdateDetailsClick = () => {
    if (!isLoading) openBillingBillingInfomationChangeMutation();
  };
  return isTabletOrLarger ? (
    <div className="fs-mask">
      <div className="billing-section__header">Billing details</div>
      <CompanyNameDetails companyName={customer.name} labelClasses={labelClasses} />
      {customer.taxId && <TaxIdDetails taxId={customer.taxId} labelClasses={labelClasses} />}
      <div className="billing-flex-table__row">
        <div className={labelClasses}>Email</div>
        <div className={valueClasses}>
          <span className="growegy-label14--moderrat" data-test="billing-details__email">
            {customer.email}
          </span>
        </div>
      </div>
      <div className="billing-flex-table__row">
        <div className={labelClasses}>Billing address</div>
        <div className={valueClasses}>
          <AddressTile address={customer.addressDto} />
        </div>
      </div>
      <div className="billing-flex-table__row">
        <div className={labelClasses}>Phone number</div>
        <div className={valueClasses}>
          <PhoneDetails phone={customer.addressDto.phone} />
        </div>
      </div>
      <div className="row common-table__row billing-flex-table__header">
        <div className={labelClasses} />
        <DivButton
          isGray={true}
          text="Update details"
          onClick={onUpdateDetailsClick}
          className="common-table__add-new-btn"
        />
      </div>
    </div>
  ) : (
    <div className="fs-mask">
      <div className="billing-section__header">Billing details</div>
      <CompanyNameDetails companyName={customer.name} labelClasses={labelClasses} />
      {customer.taxId && <TaxIdDetails taxId={customer.taxId} labelClasses={labelClasses} />}
      <div className="billing-flex-table__row">
        <div className="billing-table__column--label-value">
          <div className="growegy-title14--moderat">Email</div>
          <span className="growegy-label14--moderrat" data-test="billing-details__email">
            {customer.email}
          </span>
        </div>
      </div>
      <div className="billing-flex-table__row">
        <div className="billing-table__column--label-value">
          <div className="growegy-title14--moderat">Billing address</div>
          <AddressTile address={customer.addressDto} />
        </div>
      </div>
      <div className="billing-flex-table__row">
        <div className="billing-table__column--label-value">
          <div className="growegy-title14--moderat">Phone number</div>
          <PhoneDetails phone={customer.addressDto.phone} />
        </div>
      </div>
      <div className="row common-table__row billing-flex-table__header">
        <DivButton
          isGray={true}
          text="Update details"
          onClick={onUpdateDetailsClick}
          className="common-table__add-new-btn"
        />
      </div>
    </div>
  );
};
